/**
 * Cashflow Analytics — canonical selectors for Cash Position + Liquidity Forecast.
 *
 * Computes from invoice paidAt timestamps and PayRunRecord.releasedAt
 * (the two real cash-event sinks in the seed data). Projection uses
 * open invoice dueDates as expected inflows.
 */
import type { Job } from "@/data/types";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (d: Date) => d.toLocaleString("en-US", { month: "short" });

// ─── Cash Position ──────────────────────────────────────────────────────

export interface CashPositionResult {
  /** Net cash: collected (paid invoices) minus released pay-runs, plus opening float. */
  current: number;
  /** Prior 30-day window for delta. */
  prior: number;
  /** % change vs prior 30d. Null when prior is zero. */
  deltaPct: number | null;
  /** Total inflows in last 30d. */
  inflows30d: number;
  /** Total outflows (pay-runs) in last 30d. */
  outflows30d: number;
}

/**
 * Cash position = opening float + Σ paid-invoice amounts − Σ pay-run releases.
 * Opening float defaults to $2.5M (typical contractor cash floor) so the headline
 * stays realistic before historical inflows are fully seeded.
 */
export function computeCashPosition(
  jobs: Job[],
  payRunRecords: PayRunRecord[],
  openingFloat = 2_500_000,
): CashPositionResult {
  const now = Date.now();
  const cutoff30 = now - 30 * MS_PER_DAY;
  const cutoff60 = now - 60 * MS_PER_DAY;

  let inflowsAll = 0;
  let inflows30 = 0;
  let inflows30to60 = 0;

  for (const job of jobs) {
    for (const inv of job.financials?.invoices ?? []) {
      if (!inv.paidAt) continue;
      const ts = new Date(inv.paidAt).getTime();
      inflowsAll += inv.amount;
      if (ts >= cutoff30) inflows30 += inv.amount;
      else if (ts >= cutoff60) inflows30to60 += inv.amount;
    }
  }

  let outflowsAll = 0;
  let outflows30 = 0;
  let outflows30to60 = 0;

  for (const rec of payRunRecords) {
    const ts = new Date(rec.releasedAt).getTime();
    outflowsAll += rec.netReleased;
    if (ts >= cutoff30) outflows30 += rec.netReleased;
    else if (ts >= cutoff60) outflows30to60 += rec.netReleased;
  }

  const current = openingFloat + inflowsAll - outflowsAll;
  const prior = current - (inflows30 - outflows30) + (inflows30to60 - outflows30to60);
  const net30 = inflows30 - outflows30;
  const priorNet = inflows30to60 - outflows30to60;
  const deltaPct = prior > 0 ? ((net30 - priorNet) / Math.max(prior, 1)) * 100 : null;

  return {
    current: Math.max(current, 0),
    prior: Math.max(prior, 0),
    deltaPct,
    inflows30d: inflows30,
    outflows30d: outflows30,
  };
}

// ─── Liquidity Forecast ─────────────────────────────────────────────────

export interface LiquidityPoint {
  month: string;
  /** Cash at end of month — actual where ts ≤ now, null otherwise. */
  actual: number | null;
  /** Projected cash at end of month — null where actual exists. */
  projected: number | null;
}

export interface LiquidityForecastResult {
  points: LiquidityPoint[];
  /** Lowest projected point — drives the threshold banner. */
  projectedMin: number;
}

/**
 * Build a monthly cash curve: `monthsBack` historical (actual) + `monthsForward`
 * forward (projected). Actuals roll up paid-invoice inflows + pay-run outflows
 * working backwards from current cash. Projections add open-invoice dueDate
 * inflows; outflows extrapolate the trailing 30d release rate.
 */
export function computeLiquidityForecast(
  jobs: Job[],
  payRunRecords: PayRunRecord[],
  cashNow: number,
  monthsBack = 4,
  monthsForward = 5,
): LiquidityForecastResult {
  const now = new Date();
  const points: LiquidityPoint[] = [];

  // Build month windows.
  const months: { start: number; end: number; label: string }[] = [];
  for (let i = monthsBack; i >= 1; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const endD = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    months.push({ start: d.getTime(), end: endD.getTime(), label: monthLabel(d) });
  }
  // Current month
  const curStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const curEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  months.push({ start: curStart.getTime(), end: curEnd.getTime(), label: monthLabel(curStart) });
  // Forward
  for (let i = 1; i <= monthsForward; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const endD = new Date(now.getFullYear(), now.getMonth() + i + 1, 1);
    months.push({ start: d.getTime(), end: endD.getTime(), label: monthLabel(d) });
  }

  // Compute per-month net delta (inflows − outflows).
  const netByMonth = months.map((m) => {
    let inflow = 0;
    let outflow = 0;
    const isFuture = m.start > now.getTime();

    if (!isFuture) {
      // Actual inflows = paid invoices with paidAt in window.
      for (const job of jobs) {
        for (const inv of job.financials?.invoices ?? []) {
          if (!inv.paidAt) continue;
          const ts = new Date(inv.paidAt).getTime();
          if (ts >= m.start && ts < m.end) inflow += inv.amount;
        }
      }
      // Actual outflows = pay-run releases in window.
      for (const rec of payRunRecords) {
        const ts = new Date(rec.releasedAt).getTime();
        if (ts >= m.start && ts < m.end) outflow += rec.netReleased;
      }
    } else {
      // Projected inflows = open invoices whose dueDate lands in window.
      for (const job of jobs) {
        for (const inv of job.financials?.invoices ?? []) {
          if (inv.status === "paid") continue;
          if (!inv.dueDate) continue;
          const ts = new Date(inv.dueDate).getTime();
          if (ts >= m.start && ts < m.end) inflow += inv.amount;
        }
      }
    }
    return { ...m, inflow, outflow, net: inflow - outflow, isFuture };
  });

  // Trailing 30d outflow rate → monthly projection floor for outflows.
  const cutoff30 = now.getTime() - 30 * MS_PER_DAY;
  let trailingOut = 0;
  for (const rec of payRunRecords) {
    if (new Date(rec.releasedAt).getTime() >= cutoff30) trailingOut += rec.netReleased;
  }
  const projectedMonthlyOutflow = trailingOut > 0 ? trailingOut : 0;

  // Walk backwards from cashNow through actuals to derive historic balances,
  // then forward through projections.
  // Find index of current month.
  const curIdx = netByMonth.findIndex((m) => now.getTime() >= m.start && now.getTime() < m.end);

  // Forward walk
  const balances: (number | null)[] = new Array(netByMonth.length).fill(null);
  balances[curIdx] = cashNow;

  // Walk backwards: balance at end of month i-1 = balance at end of month i - net(i)
  for (let i = curIdx - 1; i >= 0; i -= 1) {
    const next = balances[i + 1] ?? 0;
    balances[i] = Math.max(next - netByMonth[i + 1].net, 0);
  }
  // Walk forwards with projections (subtract monthly outflow rate from projected net).
  for (let i = curIdx + 1; i < netByMonth.length; i += 1) {
    const prev = balances[i - 1] ?? cashNow;
    const projectedNet = netByMonth[i].inflow - projectedMonthlyOutflow;
    balances[i] = Math.max(prev + projectedNet, 0);
  }

  netByMonth.forEach((m, i) => {
    const bal = balances[i] ?? 0;
    points.push({
      month: m.label,
      actual: !m.isFuture ? Math.round(bal) : i === curIdx ? Math.round(bal) : null,
      projected: m.isFuture ? Math.round(bal) : i === curIdx ? Math.round(bal) : null,
    });
  });

  const projectedMin = Math.min(
    ...points.filter((p) => p.projected != null).map((p) => p.projected as number),
  );

  return { points, projectedMin };
}
